import { faFacebookSquare } from "@fortawesome/free-brands-svg-icons";
import {
  faClock,
  faEnvelope,
  faEnvelopeOpen,
  faMapMarkerAlt,
  faPhone,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import JuanEMSLogo from "../../images/JuanEMSlogo.png";
import SJDEFILogo from "../../images/SJDEFILogo.png";
import "../../css/JuanScope/VerifyEmail.css";

const OTP_DURATION = 180; // 3 minutes in seconds

function JuanISVerifyOTP() {
  const location = useLocation();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const inputsRef = useRef([]);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(OTP_DURATION);
  const [canResend, setCanResend] = useState(false);

  const { email, firstName, employeeID, fromForgotPassword } = location.state || {};
  const flowType = fromForgotPassword ? "forgot" : "login";

  useEffect(() => {
    if (!email || !employeeID) {
      toast.error("Invalid session. Redirecting to login.");
      setTimeout(() => navigate("/juan-is-login"), 2000);
      return;
    }

    const otpKey = `otp_expiry_${employeeID}_${flowType}`;
    const savedOtpExpiry = localStorage.getItem(otpKey);

    if (savedOtpExpiry) {
      const remainingTime = Math.floor((parseInt(savedOtpExpiry) - Date.now()) / 1000);
      if (remainingTime > 0) {
        setOtpCountdown(remainingTime);
        setCanResend(false);
      } else {
        setOtpCountdown(0);
        setCanResend(true);
        localStorage.removeItem(otpKey);
      }
    } else {
      const newExpiry = Date.now() + OTP_DURATION * 1000;
      localStorage.setItem(otpKey, newExpiry.toString());
      setOtpCountdown(OTP_DURATION);
      setCanResend(false);
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }, [email, employeeID, navigate, flowType]);

  useEffect(() => {
    if (otpCountdown > 0) {
      timerRef.current = setInterval(() => {
        setOtpCountdown((prev) => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            setCanResend(true);
            localStorage.removeItem(`otp_expiry_${employeeID}_${flowType}`);
            clearInterval(timerRef.current);
            return 0;
          }
          return newValue;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [otpCountdown, employeeID, flowType]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (error) setError("");
    if (value && index < 5) inputsRef.current[index + 1].focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      inputsRef.current[5].focus();
    }
  };

  const sendSystemLog = async (payload) => {
    if (!payload.action) return;
    try {
      const logResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (logResponse.ok) {
        const logResData = await logResponse.json(); // Optional
        console.log('JuanIS OTP System log recorded:', logResData.message || logResData); // Optional
      } else {
        const errData = await logResponse.json().catch(() => ({ message: 'Failed to parse log error response' })); // Optional
        console.error('Failed to record JuanIS OTP system log - Server:', logResponse.status, errData.message || errData); // Optional
      }
    } catch (logErr) {
      console.error('Failed to record JuanIS OTP system log - Network/Fetch:', logErr); // Optional
    }
  };

  const {
    firstName: initialFirstName, // Use a different name to avoid conflict if state updates it
    lastName: initialLastName,   // Get lastName from state
  } = location.state || {}; // Provide default empty object

  // State for names, initialized from location.state or default
  const [currentFirstName, setCurrentFirstName] = useState(initialFirstName || "User");
  const [currentLastName, setCurrentLastName] = useState(initialLastName || "");
  const currentEmployeeID = employeeID || "N/A";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit code");
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);

      let logPayload = {
        userID: currentEmployeeID, // From component state, initialized from location.state
        accountName: `${currentFirstName} ${currentLastName}`.trim() || email || "Unknown User",
        role: 'Faculty',
        action: 'Login Failed', // Default action for this OTP step, override only for successful login OTP
        detail: ''
    };
    const contextDescription = fromForgotPassword ? "Faculty Password Reset OTP" : "Faculty Login OTP";
    logPayload.detail = `${contextDescription} verification attempt for ${email} (Employee ID: ${currentEmployeeID}).`;

    try {
      const endpoint = fromForgotPassword
        ? "/api/faculty/verify-forgot-password-otp"
        : "/api/faculty/verify-login-otp";
      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ employeeID, otp: otpString }),
      });

      const data = await response.json();

      if (!response.ok) {
        logPayload.userID = data.user?.employeeID || currentEmployeeID; // In case backend sends user fragment on error
        logPayload.accountName = data.user?.firstName ? `${data.user.firstName} ${data.user.lastName || ''}`.trim() : logPayload.accountName;
        logPayload.detail = `${contextDescription} for ${email} (ID: ${logPayload.userID}) failed: ${data.message || "Invalid OTP or server error."}`;
        // logPayload.action remains 'Login Failed'
        await sendSystemLog(logPayload); // Log the failure

        if (data.errorType === 'otp_expired') {
          setError("Verification code has expired. Please request a new code.");
          toast.error("Verification code has expired. Please request a new code.");
          setCanResend(true);
          localStorage.removeItem(`otp_expiry_${employeeID}_${flowType}`);
        } else {
          setError(data.message || "Invalid OTP");
          toast.error(data.message || "Invalid OTP");
        }
        return;
      }

       // Update logPayload with definitive success details from server response
      logPayload.userID = data.user?.employeeID || currentEmployeeID;
      logPayload.accountName = data.user?.firstName ? `${data.user.firstName} ${data.user.lastName || ''}`.trim() : `${currentFirstName} ${currentLastName}`.trim() || email;
      if (data.user?.firstName) setCurrentFirstName(data.user.firstName); // Update local state for UI if needed
      if (data.user?.lastName) setCurrentLastName(data.user.lastName);

      if (!fromForgotPassword) {
      logPayload.action = 'Logged In'; // Set action to 'Logged In'
      logPayload.detail = `Faculty ${logPayload.accountName} (${logPayload.userID}) logged in successfully (JuanIS Login OTP Verified).`;
      await sendSystemLog(logPayload); // Log successful login

        // Store JWT and user data
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify({
          employeeID: data.user.employeeID,
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
        }));
      }

      setSuccess(fromForgotPassword
        ? "OTP verified successfully! A new password has been sent to your email."
        : "OTP verified successfully!");
      toast.success(fromForgotPassword
        ? "OTP verified successfully! A new password has been sent to your email."
        : "OTP verified successfully!");
      setOtp(["", "", "", "", "", ""]);
      localStorage.removeItem(`otp_expiry_${employeeID}_${flowType}`);
      setTimeout(() => {
        navigate(fromForgotPassword ? "/juan-is-login" : "/juan-is-faculty-dashboard", {
          state: {
            email: data.user.email,
            employeeID: data.user.employeeID,
            firstName: data.user.firstName,
            lastName: data.user.lastName,
          },
        });
      }, 2000);
      await sendSystemLog(logPayload); // Log success
    } catch (error) {
      console.error("OTP verification error:", error);
      logPayload.detail = `${contextDescription} for ${email} (ID: ${currentEmployeeID}) failed with client/network error: ${error.message || "Unknown client error."}`;
      await sendSystemLog(logPayload); 
      setError("Failed to verify OTP. Please check your network or try again.");
      toast.error("Failed to verify OTP. Please check your network or try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setResendLoading(true);
    setError("");
    setSuccess("");

    try {
      const endpoint = fromForgotPassword ? "/api/faculty/forgot-password-otp" : "/api/faculty/resend-otp";
      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Content-Type-Options": "nosniff",
          "X-XSS-Protection": "1; mode=block",
        },
        body: JSON.stringify({ employeeID }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Failed to resend OTP");
        toast.error(data.message || "Failed to resend OTP");
        return;
      }

      const newExpiry = Date.now() + OTP_DURATION * 1000;
      localStorage.setItem(`otp_expiry_${employeeID}_${flowType}`, newExpiry.toString());
      setOtpCountdown(OTP_DURATION);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
      inputsRef.current[0].focus();
      setSuccess("New verification code sent to your email");
      toast.success("New verification code sent to your email");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Resend OTP error:", error);
      setError("Failed to resend OTP. Please check your network or try again.");
      toast.error("Failed to resend OTP. Please check your network or try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const getVerificationTitle = () => {
    return fromForgotPassword ? "Verify to Reset Password" : "Verify Your Login";
  };

  const getVerificationDescription = () => {
    return `Please enter the 6-digit verification code sent to ${email} to complete your ${fromForgotPassword ? "password reset" : "login"}.`;
  };

  return (
    <div className="juan-verify-container">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <header className="juan-register-header">
        <div className="juan-header-left">
          <img
            src={SJDEFILogo}
            alt="SJDEFI Logo"
            className="juan-logo-register"
          />
          <div className="juan-header-text">
            <h1>JUAN IS</h1>
            <p className="juan-footer-motto">
              Where faith and reason are expressed in Charity.
            </p>
          </div>
        </div>
      </header>

      <div className="juan-verify-main">
        <div className="juan-verify-card">
          <FontAwesomeIcon
            icon={faEnvelopeOpen}
            size="3x"
            className="juan-verify-icon"
          />
          <h2>{getVerificationTitle()}</h2>
          <p className="juan-verify-description">
            {getVerificationDescription()}
          </p>

          <form onSubmit={handleSubmit} className="juan-otp-form">
            <div className="juan-otp-inputs" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  ref={(el) => (inputsRef.current[index] = el)}
                  className="juan-otp-input"
                  disabled={loading}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {otpCountdown > 0 && (
              <p className="juan-otp-timer">
                Code expires in: {formatTime(otpCountdown)}
              </p>
            )}

            {otpCountdown <= 0 && (
              <p className="juan-otp-warning">
                This code has expired. Please request a new code.
              </p>
            )}

            {error && <p className="juan-otp-error">{error}</p>}
            {success && <p className="juan-otp-success">{success}</p>}

            <div className="juan-otp-actions">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading || !canResend}
                className="juan-resend-button"
              >
                {resendLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  "Resend Code"
                )}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="juan-verify-button"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Verify"}
              </button>
            </div>
          </form>

          <p className="juan-verify-note">
            If you don't receive the code, check your spam folder or click Resend Code.
          </p>
        </div>
      </div>

      <footer className="juan-register-footer">
        <div className="juan-footer-left">
          <img src={JuanEMSLogo} alt="JuanEMS Logo" className="juan-footer-logo" />
          <div className="juan-footer-text">
            <h1>JuanEMS - JUAN IS</h1>
            <p className="juan-footer-motto">
              2025. San Juan De Dios Educational Foundation Inc.
            </p>
          </div>
        </div>
        <div className="juan-footer-content">
          <div className="juan-footer-links">
            <a href="/about" className="footer-link">About</a>
            <span className="footer-link-separator"> | </span>
            <a href="/terms-of-use" className="footer-link">Terms of Use</a>
            <span className="footer-link-separator"> | </span>
            <a href="/privacy" className="footer-link">Privacy</a>
          </div>
          <a
            href="https://www.facebook.com/SJDEFIcollege"
            target="_blank"
            rel="noopener noreferrer"
            className="juan-footer-social-link"
          >
            <FontAwesomeIcon icon={faFacebookSquare} className="juan-social-icon" />
            <div className="juan-social-text">
              <span className="juan-social-find">Find us on</span>
              <span className="juan-social-platform">Facebook</span>
            </div>
          </a>
          <div className="juan-footer-contact-container">
            <div className="juan-contact-title">
              <FontAwesomeIcon icon={faPhone} />
              <span>CONTACT US</span>
            </div>
            <div className="juan-contact-items">
              <div className="juan-contact-item">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>2772 Roxas Blvd., Pasay City, Philippines, 1300</span>
              </div>
              <div className="juan-contact-item">
                <FontAwesomeIcon icon={faPhone} />
                <span>+632 551-2763</span>
              </div>
              <div className="juan-contact-item">
                <FontAwesomeIcon icon={faEnvelope} />
                <span>
                  admission_office@sjdefi.edu.ph | registrarsoffice@sjdefi.edu.ph
                </span>
              </div>
              <div className="juan-contact-item">
                <FontAwesomeIcon icon={faClock} />
                <span>
                  Monday to Thursday - 7:00 AM to 5:00 PM | Friday - 7:00 AM to 4:00 PM
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default JuanISVerifyOTP;